import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { LinkedIn, Twitter, Instagram, LinkOff } from '@mui/icons-material';
import { PlatformDraft } from '../../types/api';
import { getPlatformLabel } from '../../constants/platforms';
import { PUBLISH_PLATFORMS } from '../../utils/publishPlatforms';

const PLATFORM_META: Record<
  string,
  { icon: React.ReactElement; color: string }
> = {
  linkedin: { icon: <LinkedIn fontSize="small" />, color: '#0077B5' },
  twitter: { icon: <Twitter fontSize="small" />, color: '#000000' },
  instagram: { icon: <Instagram fontSize="small" />, color: '#E4405F' },
};

interface MultiPublishPanelProps {
  platformDrafts: Record<string, PlatformDraft>;
  connectedDraftKeys: Set<string>;
  selectedPlatforms: string[];
  onSelectionChange: (platforms: string[]) => void;
}

export const MultiPublishPanel: React.FC<MultiPublishPanelProps> = ({
  platformDrafts,
  connectedDraftKeys,
  selectedPlatforms,
  onSelectionChange,
}) => {
  const togglePlatform = (platform: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedPlatforms, platform]);
    } else {
      onSelectionChange(selectedPlatforms.filter((p) => p !== platform));
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Select platforms to publish
      </Typography>
      {PUBLISH_PLATFORMS.map((platform) => {
        const draft = platformDrafts[platform];
        const meta = PLATFORM_META[platform];
        const hasContent = Boolean(draft?.content?.trim());
        const isConnected = connectedDraftKeys.has(platform);
        const overLimit = draft && !draft.within_limit;
        const disabled = !hasContent || !isConnected || overLimit;

        let helper = '';
        if (!hasContent) helper = 'No content for this platform';
        else if (!isConnected) helper = 'Connect account in Integrations';
        else if (overLimit) helper = `Over ${draft?.char_limit} char limit`;

        return (
          <Box
            key={platform}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedPlatforms.includes(platform)}
                  onChange={(e) => togglePlatform(platform, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: meta?.color }}>
                    {meta?.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">{getPlatformLabel(platform)}</Typography>
                    {draft?.content && (
                      <Typography variant="caption" color="text.secondary">
                        {draft.char_count}/{draft.char_limit} chars
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
            />
            {!isConnected && hasContent && (
              <LinkOff fontSize="small" color="disabled" titleAccess="Not connected" />
            )}
            {helper && (
              <Typography variant="caption" color={overLimit ? 'error' : 'text.secondary'}>
                {helper}
              </Typography>
            )}
          </Box>
        );
      })}
      {selectedPlatforms.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Select at least one connected platform with valid content.
        </Alert>
      )}
    </Box>
  );
};

export default MultiPublishPanel;
